<script lang="ts" context="module">
  /**
   * Props Convenience Type Export
   * ---------------------------------------------------------------------------
   */

  import type { ComponentProps } from "svelte";
  import CardAvatar from "./CardAvatar.svelte"; // self reference
  export type CardAvatarProps = ComponentProps<CardAvatar>;
</script>

<script lang="ts">
  /**
   * Imports
   * ---------------------------------------------------------------------------
   */

  import { twJoin } from "tailwind-merge";

  /**
   * Types
   * ---------------------------------------------------------------------------
   */

  type Variant = "dark" | "darkGhost";

  /**
   * Component: CardAvatar
   * ---------------------------------------------------------------------------
   * Render a round avatar with an image or initials.
   */

  /**
   * Props
   * ---------------------------------------------------------------------------
   */

  /**
   * avatar image source URL;
   * if not defined, fall back to initials with background color
   */
  export let imageSrc: string | undefined = undefined;

  /** initials */
  export let initials: string | undefined = undefined;

  /** name */
  export let name: string | undefined = undefined;

  /** variant presets */
  export let variant: Variant = "dark";

  if (!initials && name) {
    initials = name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }

  /**
   * Data
   * ---------------------------------------------------------------------------
   */
  const variantMixins: {
    [key in Variant]: string;
  } = {
    darkGhost: "bg-black/50", // semi-transparent black
    dark: "bg-n900", // opaque dark neutral
  };
</script>

<!-- Template ----------------------------------------------------------------->

<div class="flex flex-row items-center gap-2">
  {#if initials || imageSrc}
    <div
      class={twJoin(
        "size-10 min-w-10 rounded-full flex flex-col justify-center items-center",
        variantMixins[variant],
      )}
    >
      {#if imageSrc}
        <img src={imageSrc} alt="Avatar" class={"w-full h-full object-cover"} />
      {:else if initials}
        <p class={"text-white text-center"}>
          {initials}
        </p>
      {/if}
    </div>
  {/if}
  {#if name}
    <p class="text-white wah-body-small">{name}</p>
  {/if}
</div>
